import '../pop';
import { defineCustomElement, getBooleanAttribute, getLiteralAttribute, updateLiteralAttribute, updateBooleanAttribute, NectaryElement, updateAttribute, getReactEventHandler, isAttrTrue, setClass, rectOverlap, subscribeContext, isAttrEqual } from '../utils';
const templateHTML = '<style>:host{display:contents}#content-wrapper{position:relative;padding-top:4px}:host([tip]) #content-wrapper{padding-top:12px;filter:drop-shadow(var(--sinch-comp-popover-shadow))}:host([orientation^=top]) #content-wrapper{padding-top:0;padding-bottom:4px}:host([orientation^=top][tip]) #content-wrapper{padding-top:0;padding-bottom:12px}#content{background-color:var(--sinch-comp-popover-color-default-background-initial);border:1px solid var(--sinch-comp-popover-color-default-border-initial);border-radius:var(--sinch-comp-popover-shape-radius);box-shadow:var(--sinch-comp-popover-shadow);overflow:hidden}:host([tip]) #content{box-shadow:none}#tip{position:absolute;left:50%;top:13px;transform:translateX(-50%) rotate(180deg);transform-origin:top center;fill:var(--sinch-comp-popover-color-default-background-initial);stroke:var(--sinch-comp-popover-color-default-border-initial);stroke-linecap:square;pointer-events:none;display:none}:host([orientation^=top]) #tip{transform:translateX(-50%) rotate(0);top:calc(100% - 13px)}:host([tip]) #tip:not(.hidden){display:block}</style><sinch-pop id="pop" inset="4"><slot name="target" slot="target"></slot><div id="content-wrapper" slot="content"><div id="content"><slot name="content"></slot></div><svg id="tip" width="16" height="9" aria-hidden="true"><path d="m0 0 8 8 8 -8"/></svg></div></sinch-pop>';
import { getPopOrientation, orientationValues } from './utils';
const TIP_SIZE = 16;
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-popover', class extends NectaryElement {
  #$pop;
  #$content;
  #$tip;
  #controller = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$pop = shadowRoot.querySelector('#pop');
    this.#$content = shadowRoot.querySelector('#content');
    this.#$tip = shadowRoot.querySelector('#tip');
  }
  connectedCallback() {
    this.#controller = new AbortController();
    const {
      signal
    } = this.#controller;
    this.addEventListener('-close', this.#onCloseReactHandler, {
      signal
    });
    this.#$pop.addEventListener('-close', this.#onPopClose, {
      signal
    });
    subscribeContext(this.#$content, 'visibility', this.#onContextVisibility, signal);
    updateAttribute(this.#$pop, 'orientation', getPopOrientation(this.orientation));
  }
  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['orientation', 'open', 'modal', 'tip', 'aria-label', 'aria-description'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (isAttrEqual(oldVal, newVal)) {
      return;
    }
    switch (name) {
      case 'orientation':
        {
          updateAttribute(this.#$pop, 'orientation', getPopOrientation(this.orientation));
          if (this.#$pop.open) {
            this.#updateTipOrientation();
          }
          break;
        }
      case 'tip':
        {
          const hasTip = isAttrTrue(newVal);
          if (hasTip && this.#$pop.open) {
            this.#updateTipOrientation();
          }
          updateBooleanAttribute(this, name, hasTip);
          break;
        }
      case 'modal':
      case 'open':
        {
          updateAttribute(this.#$pop, name, newVal);
          updateBooleanAttribute(this, name, isAttrTrue(newVal));
          break;
        }
      case 'aria-label':
      case 'aria-description':
        {
          updateAttribute(this.#$pop, name, newVal);
          break;
        }
    }
  }
  set modal(isModal) {
    updateBooleanAttribute(this, 'modal', isModal);
  }
  get modal() {
    return getBooleanAttribute(this, 'modal');
  }
  set open(isOpen) {
    updateBooleanAttribute(this, 'open', isOpen);
  }
  get open() {
    return getBooleanAttribute(this, 'open');
  }
  set tip(hasTip) {
    updateBooleanAttribute(this, 'tip', hasTip);
  }
  get tip() {
    return getBooleanAttribute(this, 'tip');
  }
  get orientation() {
    return getLiteralAttribute(this, orientationValues, 'orientation', 'bottom-left');
  }
  set orientation(value) {
    updateLiteralAttribute(this, orientationValues, 'orientation', value);
  }
  get footprintRect() {
    return this.#$pop.footprintRect;
  }
  get popoverRect() {
    return this.#$pop.popoverRect;
  }
  #onPopClose = () => {
    this.#dispatchCloseEvent();
  };
  #onCloseReactHandler = e => {
    getReactEventHandler(this, 'onClose')?.();
    getReactEventHandler(this, 'on-close')?.(e);
  };
  #dispatchCloseEvent() {
    this.dispatchEvent(new CustomEvent('-close'));
  }
  #onContextVisibility = e => {
    if (e.detail) {
      this.#updateTipOrientation();
    } else {
      this.#resetTipOrientation();
    }
  };
  #resetTipOrientation() {
    this.#$tip.style.top = '';
    this.#$tip.style.left = '';
  }
  #updateTipOrientation = () => {
    const orientation = this.orientation;
    const targetRect = this.#$pop.footprintRect;
    const contentRect = this.#$content.getBoundingClientRect();
    const diffX = targetRect.x - contentRect.x;
    let desiredX = diffX + targetRect.width / 2;
    if (orientation === 'bottom-left' || orientation === 'top-left') {
      desiredX = Math.max(desiredX, contentRect.width * 0.75);
    }
    if (orientation === 'bottom-right' || orientation === 'top-right') {
      desiredX = Math.min(desiredX, contentRect.width * 0.25);
    }
    const xPos = Math.max(TIP_SIZE, Math.min(desiredX, contentRect.width - TIP_SIZE));
    this.#$tip.style.left = `${xPos}px`;
    setClass(this.#$tip, 'hidden', rectOverlap(targetRect, contentRect));
  };
});